import type { ComponentProps } from "react";

import { cva, type VariantProps } from "class-variance-authority";

import {
	CheckCircleIcon,
	CircleAlertIcon,
	TriangleAlertIcon,
	InfoIcon
} from "lucide-react";

import { cn } from "../../lib/utils";

const alertVariants = cva(
	"relative flex w-full items-start gap-2 rounded-lg border p-2.5 [&>svg]:size-4.5 [&>svg]:shrink-0 [&>svg]:translate-y-[1px]",
	{
		variants: {
			variant: {
				info: "border-sky-500/30 bg-sky-500/10 text-sky-900 dark:text-sky-200 [&>svg]:text-sky-500",
				success:
					"border-emerald-500/30 bg-emerald-500/10 text-emerald-900 dark:text-emerald-200 [&>svg]:text-emerald-500",
				warning:
					"border-amber-500/30 bg-amber-500/10 text-amber-900 dark:text-amber-200 [&>svg]:text-amber-500",
				destructive:
					"border-red-500/30 bg-red-500/10 text-red-900 dark:text-red-200 [&>svg]:text-red-500"
			}
		},
		defaultVariants: {
			variant: "info"
		}
	}
);

const IconMap = {
	info: InfoIcon,
	success: CheckCircleIcon,
	warning: TriangleAlertIcon,
	destructive: CircleAlertIcon
};

type AlertProps = ComponentProps<"div"> & VariantProps<typeof alertVariants>;

export function Alert({ className, variant, children, ...props }: AlertProps) {
	const Icon = IconMap[variant as keyof typeof IconMap];

	return (
		<div
			data-slot="alert"
			role="alert"
			className={cn(alertVariants({ variant }), className)}
			{...props}
		>
			<Icon className="size-4" />
			<div className="flex w-full flex-col">{children}</div>
		</div>
	);
}

type AlertTitleProps = ComponentProps<"div">;

export function AlertTitle({ className, ...props }: AlertTitleProps) {
	return <div data-slot="alert-title" className={cn("text-sm font-medium", className)} {...props} />;
}

type AlertDescriptionProps = ComponentProps<"div">;

export function AlertDescription({ className, ...props }: AlertDescriptionProps) {
	return <div data-slot="alert-description" className={cn("text-sm", className)} {...props} />;
}